import lodash from 'lodash';
import { composeReducers } from 'common/util/composeReducers';
import {
    LOAD_STUDY_EXPECTATIONS,
    LOAD_STAFF_DOCUMENT_TYPES,
    EVALUATE_STUDY_EXPECTATIONS,
    PROCESSING_STUDY_EXPECTATIONS,
    PROCESSING_STAFF_DOCUMENT_TYPES,
    UNLOAD_STUDY_EXPECTATIONS,
    EVALUATING_STUDY_EXPECTATIONS,
    ADD_UPLOADED_DOCUMENTS_TO_STUDY_EXPECTATIONS,
    UNLOAD_UPLOADED_DOCUMENTS,
} from './../actions/studyExpectations';

const DOCUMENT_TYPE_ID = 'documentType.id';
const MISSING = 'MISSING';
const STUDY_ROLE_ID = 'studyRoleId';

export function processingStudyExpectations(state, action) {
    if (!action || action.type !== PROCESSING_STUDY_EXPECTATIONS) {
        return state;
    }
    return {
        ...state,
        processingStudyExpectations: action.payload
    };
}

export function processingStaffDocumentTypes(state, action) {
    if (!action || action.type !== PROCESSING_STAFF_DOCUMENT_TYPES) {
        return state;
    }
    return {
        ...state,
        processingStaffExpectations: action.payload
    };
}

export function evaluatingStudyExpectations(state, action) {
    if (!action || action.type !== EVALUATING_STUDY_EXPECTATIONS) {
        return state;
    }
    return {
        ...state,
        studyExpectations: {
            ...state.studyExpectations,
            isLoading: action.payload,
        }
    };
}

export function evaluateStudyExpectations(state, action) {
    if (!action || action.type !== EVALUATE_STUDY_EXPECTATIONS) {
        return state;
    }

    const {
        studyExpectations,
        studyExpectationStatuses,
        documents
    } = action.payload;

    const studyTeam = lodash.get(state, 'studySettings.studyTeam', []);

    const {
        allStudy,
        missingStudy,
        missingStudyCount,
        allStaff,
        missingStaff,
        missingStaffCount,
    } = evaluateStudyExpectationsHelper(
        state,
        studyExpectations,
        studyExpectationStatuses,
        studyTeam,
        documents);

    return {
        ...state,
        studyExpectations: {
            allExpectedStudyDocuments: allStudy,
            allExpectedStaffDocuments: allStaff,
            missingExpectedStudyDocuments: missingStudy,
            missingExpectedStaffDocuments: missingStaff,
            missingStudyCount, 
            missingStaffCount,
            missingExpectationsCount: missingStudyCount + missingStaffCount,
        }
    };
}

export function assignDuplicatesDocumentsToStudyExpectations(state, action) {
    if (!action || action.type !== ADD_UPLOADED_DOCUMENTS_TO_STUDY_EXPECTATIONS) {
        return state;
    }

    const { studyExpectation, lastUploadedDocument, foundDuplicates } = action.payload;

    const duplicates = lodash.head(foundDuplicates).duplicates;
    const lastUploadedDocumentWithDuplicates = { ...lastUploadedDocument, duplicates, };

    const studyTeam = lodash.get(state, 'studySettings.studyTeam', []);

    const allExpectedStudyDocuments = lodash.flatMap(lodash.get(state, 'studyExpectations.allExpectedStudyDocuments', []));
    const allExpectedStaffDocuments = lodash.flatMap(lodash.get(state, 'studyExpectations.allExpectedStaffDocuments', []));

    const newAllExpectedStudyDocuments = assignUploadedToStudyExpectation(lastUploadedDocumentWithDuplicates, studyExpectation.id, allExpectedStudyDocuments);
    const newAllExpectedStaffDocuments = assignUploadedToStudyExpectation(lastUploadedDocumentWithDuplicates, studyExpectation.id, allExpectedStaffDocuments);

    const { allStudy, missingStudy, missingStudyCount } = formatStudyExpectations(newAllExpectedStudyDocuments);
    const { allStaff, missingStaff, missingStaffCount } = formatStaffExpectations(newAllExpectedStaffDocuments, studyTeam);

    return {
        ...state,
        studyExpectations: {
            ...state.studyExpectations,
            allExpectedStudyDocuments: allStudy,
            allExpectedStaffDocuments: allStaff,
            missingExpectedStudyDocuments: missingStudy,
            missingExpectedStaffDocuments: missingStaff,
            missingStudyCount,
            missingStaffCount,
            missingExpectationsCount: missingStudyCount + missingStaffCount,
        }
    };
}

export function unloadUploadedDocuments(state, action) {
    if (!action || action.type !== UNLOAD_UPLOADED_DOCUMENTS) {
        return state;
    }

    const studyExpectation = action.payload;

    const {
        files: {
            hasNewDocumentConversionResult, ...otherProperties
        },
        studyExpectations: {
            allExpectedStudyDocuments,
            allExpectedStaffDocuments,
            missingExpectedStudyDocuments,
            missingExpectedStaffDocuments,
        },
    } = state;

    const editedFiles = {
        hasNewDocumentConversionResult: false, ...otherProperties
    };

    allExpectedStudyDocuments.forEach(all => {
        if (all.id === studyExpectation.id) all.uploaded = undefined;
    });

    for (const value of Object.values(allExpectedStaffDocuments)) {
        value.forEach(v => {
            if (v.id === studyExpectation.id) v.uploaded = undefined;
        });
    }

    missingExpectedStudyDocuments.forEach(missing => {
        if (missing.id === studyExpectation.id) missing.uploaded = undefined;
    });

    for (const value of Object.values(missingExpectedStaffDocuments)) {
        value.forEach(v => {
            if (v.id === studyExpectation.id) v.uploaded = undefined;
        });
    }

    return {
        ...state,
        files: editedFiles,
        studyExpectations: {
            ...state.studyExpectations,
            allExpectedStudyDocuments,
            allExpectedStaffDocuments,
            missingExpectedStudyDocuments,
            missingExpectedStaffDocuments,
        },
    };
}

export function loadStudyExpectations(state, action) {
    if (!action || action.type !== LOAD_STUDY_EXPECTATIONS) {
        return state;
    }
    const { studyExpectations } = action.payload;

    const sortedStudyExpectations = lodash.orderBy(studyExpectations, ['name']);
    const [studyDocumentExpectations, staffDocumentExpectations] = lodash.partition(
        sortedStudyExpectations,
        expectation => lodash.isNil(expectation.studyRoleId)
    );


    return {
        ...state,
        study: {
            ...state.study,
            studyExpectations: sortedStudyExpectations,
            staffDocumentExpectations,
            studyDocumentExpectations,
        }
    };
}

export function loadStaffDocumentTypes(state, action) {
    if (!action || action.type !== LOAD_STAFF_DOCUMENT_TYPES) {
        return state;
    }

    return {
        ...state,
        staffDocumentTypes: action.payload,
    };
}

export function unloadStudyExpectations(state, action) {
    if (!action || action.type !== UNLOAD_STUDY_EXPECTATIONS) {
        return state;
    }
    return lodash.omit(state, 'studyExpectations');
}

function assignUploadedToStudyExpectation(lastUploadedDocument, studyExpectationId, studyExpectations) {
    return lodash.map(studyExpectations, expectation => {
        if (expectation.id === studyExpectationId) {
            expectation.uploaded = [...expectation.uploaded || [], lastUploadedDocument];
        }

        return expectation;
    });
}

function evaluateDocumentAccess(studyExpectations, state) {
    const documentTypes = lodash.get(state, 'me.documentTypes', []);
    return lodash.map(studyExpectations, expectation => {
        expectation.documentAccess = lodash.get(
            documentTypes,
            `${expectation.sourceId}.permissions.viewPDF`,
            false);
        return expectation;
    });
}

function evaluateStudyExpectationsHelper(
    state,
    expectations,
    expectationStatuses,
    studyTeam,
    documents
) {
    const expectationsWithStatuses = addStatusToExpectations(expectations, expectationStatuses);
    const expectationsWithDocuments = attachFiledDocumentsToExpectations(expectationsWithStatuses, documents);
    const expectationsWithAccess = evaluateDocumentAccess(expectationsWithDocuments, state);

    const { studyExpectations, staffExpectations } = splitStudyAndStaffExpectations(expectationsWithAccess);
    const { allStudy, missingStudy, missingStudyCount } = formatStudyExpectations(studyExpectations);
    const { allStaff, missingStaff, missingStaffCount } = formatStaffExpectations(staffExpectations, studyTeam);

    return { allStudy, missingStudy, missingStudyCount, allStaff, missingStaff, missingStaffCount };
}

function addStatusToExpectations(expectations, expectationStatuses) {
    return expectations.map(exp => {
        const foundStatus = expectationStatuses.filter(status => status.studyExpectationId === exp.id);
        return { ...exp, status: foundStatus };
    });
}

function attachFiledDocumentsToExpectations(expectations, documents) {
    const groupedDocuments = lodash.groupBy(documents, DOCUMENT_TYPE_ID);

    return expectations.map(exp => {
        if (exp.sourceId in groupedDocuments) return { ...exp, documents: groupedDocuments[exp.sourceId] };
        return { ...exp };
    });
}

function splitStudyAndStaffExpectations(expectations) {
    const studyExpectations = [], staffExpectations = [];

    lodash.map(expectations, expectation => {
        const isStaffExpectation = !!(expectation.studyRoleId || expectation.studyTeamMemberId);
        return isStaffExpectation ? staffExpectations.push(expectation) : studyExpectations.push(expectation);
    });

    return { studyExpectations, staffExpectations };
}

function formatStudyExpectations(study) {
    const allStudy = study;
    const missingStudy = study.filter(expectation => expectation.status.some(status => status.status === MISSING));

    return { allStudy, missingStudy, missingStudyCount: missingStudy.length || 0 };
}

function formatStaffExpectations(staff, studyTeam) {
    const groupExpectationsByRole = lodash.groupBy(staff, STUDY_ROLE_ID);
    let count = 0;
    const missing = {};
    for (const [key, value] of Object.entries(groupExpectationsByRole)) {
        const matchingRoles = studyTeam.filter(s => s.studyRoleId === key);
        value.forEach(expectation => {            
            expectation.documents = matchingRoles.flatMap(matchingRole => [...filterDocumentsByMatchingStudyTeamMember(expectation, matchingRole)]);
            count += expectation.status.reduce((accumulator, currentValue) => accumulator + (currentValue.status === MISSING), 0);    

        });
        missing[key] = lodash.compact(value.map(expectation => {
            const statuses = expectation.status.filter(stat => stat.status === MISSING);
            if(statuses.length === 0) return;            
            return {
                ...expectation,
                status: statuses,
            };
        }));
    }

    const allStaff = groupExpectationsByRole;

    return {
        allStaff,
        missingStaff: missing,
        missingStaffCount: count,
    };
}

function filterDocumentsByMatchingStudyTeamMember(expectation, teamMember) {
    const documents = [];    
    (expectation.documents||[]).forEach(document => {
        const documentDescriptors = [];

        document.descriptors.forEach(d => documentDescriptors.push(...d.values));

        if (documentDescriptors.includes(teamMember.nameOfResearcher) && !documents.some(doc => doc.id === document.id)) {
            documents.push({...document, teamMemberId: teamMember.id});
        }
    });

    return documents;
}

export default composeReducers(
    loadStudyExpectations,
    loadStaffDocumentTypes,
    processingStudyExpectations,
    processingStaffDocumentTypes,
    evaluateStudyExpectations,
    unloadStudyExpectations,
    evaluatingStudyExpectations,
    assignDuplicatesDocumentsToStudyExpectations,
    unloadUploadedDocuments,
);
